import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Facebook, Instagram, Youtube, Heart, ExternalLink, RefreshCw } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { SOCIAL_LINKS } from "../data/siteData";
import { Reveal, EASE } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_META = {
    facebook: { label: "Facebook", icon: Facebook, color: "#1877F2", link: SOCIAL_LINKS.facebook },
    instagram: { label: "Instagram", icon: Instagram, color: "#E1306C", link: SOCIAL_LINKS.instagram },
    youtube: { label: "YouTube", icon: Youtube, color: "#FF0000", link: SOCIAL_LINKS.youtube },
};

const formatDate = (iso) =>
    new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });

const PostCard = ({ post, index }) => {
    const meta = PLATFORM_META[post.platform];
    const Icon = meta.icon;
    return (
        <motion.article
            data-testid={`social-post-${post.id}`}
            className="bg-white border border-zinc-200 hover:border-[#0E0E10] transition-colors duration-300 flex flex-col"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: (index % 3) * 0.08, ease: EASE }}
        >
            <div className="relative aspect-[4/3] overflow-hidden">
                <img src={post.image} alt={post.text} loading="lazy" className="w-full h-full object-cover" />
                <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-white/95 text-xs font-semibold px-2.5 py-1.5" style={{ color: meta.color }}>
                    <Icon size={14} /> {meta.label}
                </span>
            </div>
            <div className="p-5 flex flex-col flex-1">
                <p className="text-sm leading-relaxed text-zinc-700 flex-1">{post.text}</p>
                <div className="mt-4 flex items-center justify-between text-xs text-zinc-500">
                    <span className="inline-flex items-center gap-1.5">
                        <Heart size={13} className="text-[#FF0066]" /> {post.likes}
                    </span>
                    <span>{formatDate(post.posted_at)}</span>
                </div>
                <a
                    href={post.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`social-post-link-${post.id}`}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0E0E10] hover:text-[#FF0066] transition-colors duration-200"
                >
                    Vedi il post <ExternalLink size={14} />
                </a>
            </div>
        </motion.article>
    );
};

export const SocialFeed = () => {
    const [posts, setPosts] = useState([]);

    useEffect(() => {
        axios.get(`${API}/social-feed`).then((res) => setPosts(res.data)).catch(() => {});
    }, []);

    return (
        <section id="social-feed" data-testid="social-feed-section" className="py-16 sm:py-24 lg:py-32 bg-white border-y border-zinc-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
                    <Reveal className="max-w-2xl">
                        <p className="font-mono-label text-xs text-[#FF0066] mb-4">Social</p>
                        <h2 data-testid="social-feed-title" className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight text-[#0E0E10]">
                            Segui il laboratorio in tempo reale
                        </h2>
                        <p className="mt-4 text-base sm:text-lg text-zinc-600 leading-relaxed">
                            Gli ultimi lavori pubblicati sulle nostre pagine social, direttamente qui.
                        </p>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <span data-testid="social-sync-badge" className="inline-flex items-center gap-2 text-xs font-mono-label text-zinc-500 border border-zinc-300 px-4 py-2">
                            <RefreshCw size={13} className="text-[#0099FF]" /> Aggiornato in tempo reale
                        </span>
                    </Reveal>
                </div>

                <Tabs defaultValue="all" className="mt-10">
                    <TabsList data-testid="social-feed-tabs" className="bg-[#FAF8F5] border border-zinc-200 p-1 h-auto flex-wrap">
                        <TabsTrigger value="all" data-testid="social-feed-tab-all" className="text-xs font-mono-label px-4 py-2 data-[state=active]:bg-[#0E0E10] data-[state=active]:text-white">Tutti</TabsTrigger>
                        {Object.entries(PLATFORM_META).map(([key, meta]) => (
                            <TabsTrigger key={key} value={key} data-testid={`social-feed-tab-${key}`} className="text-xs font-mono-label px-4 py-2 gap-1.5 data-[state=active]:bg-[#0E0E10] data-[state=active]:text-white">
                                <meta.icon size={13} /> {meta.label}
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    <TabsContent value="all" className="mt-8">
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {posts.map((post, i) => <PostCard key={post.id} post={post} index={i} />)}
                        </div>
                    </TabsContent>
                    {Object.keys(PLATFORM_META).map((key) => (
                        <TabsContent key={key} value={key} className="mt-8">
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {posts.filter((p) => p.platform === key).map((post, i) => <PostCard key={post.id} post={post} index={i} />)}
                            </div>
                        </TabsContent>
                    ))}
                </Tabs>
            </div>
        </section>
    );
};
